import type { Role, TeamDTO, UserDTO } from '@copilot/shared';
import { InviteBodySchema, RoleSchema } from '@copilot/shared';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { usersApi, teamsApi } from '../api/users';
import type { UserPatch } from '../api/types';
import { toUserDraft, userPatchFromDraft, type UserDraft } from '../lib/users';
import { useUser } from '../auth/AuthProvider';
import { ROLE_LABELS } from '../auth/roles';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  ErrorBanner,
  Field,
  Input,
  ListSkeleton,
  PageHeader,
  Select,
  Table,
  Td,
  Th,
  Tr,
} from '../components';
import { formatUsd } from '../lib/format';

export function UsersPage() {
  const me = useUser();
  const isAdmin = me.role === 'admin';
  const users = useQuery({ queryKey: ['users'], queryFn: usersApi.list });
  const teams = useQuery({ queryKey: ['teams'], queryFn: teamsApi.list });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Users & teams"
        description={isAdmin ? undefined : 'Read-only view of your team. Ask an admin to make changes.'}
      />
      <Card title="Users" bodyClassName="p-0">
        {users.isError && <ErrorBanner error={users.error} onRetry={() => void users.refetch()} className="m-3" />}
        {users.isLoading ? (
          <ListSkeleton />
        ) : users.data?.length === 0 ? (
          <EmptyState title="No users" />
        ) : users.data ? (
          <Table>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Role</Th>
                <Th>Team</Th>
                <Th>Active</Th>
                <Th className="text-right">Monthly limit</Th>
                <Th>Cloud STT</Th>
                {isAdmin && <Th className="w-32" />}
              </tr>
            </thead>
            <tbody>
              {[...users.data]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((u) => (
                  <UserRow key={u.id} user={u} teams={teams.data ?? []} editable={isAdmin} isSelf={u.id === me.id} />
                ))}
            </tbody>
          </Table>
        ) : null}
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        {isAdmin && <InviteCard teams={teams.data ?? []} />}
        <TeamsCard teams={teams} editable={isAdmin} />
      </div>
    </div>
  );
}

function UserRow({ user, teams, editable, isSelf }: { user: UserDTO; teams: TeamDTO[]; editable: boolean; isSelf: boolean }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<UserDraft | null>(null);
  const [error, setError] = useState<unknown>(null);
  const resetPassword = useMutation({
    mutationFn: () => usersApi.resetPassword(user.id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['users'] }),
  });
  const save = useMutation({
    mutationFn: (patch: UserPatch) => usersApi.patch(user.id, patch),
    onSuccess: () => {
      setDraft(null);
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      void queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
    onError: setError,
  });

  const submit = () => {
    if (!draft) return;
    const patch = userPatchFromDraft(user, draft);
    if (typeof patch === 'string') return setError(new Error(patch));
    if (Object.keys(patch).length === 0) return setDraft(null);
    save.mutate(patch);
  };

  if (draft) {
    const set = (p: Partial<UserDraft>) => setDraft({ ...draft, ...p });
    return (
      <>
        <tr className="bg-accent-soft/30">
          <Td>
            <Input aria-label="Name" value={draft.name} onChange={(e) => set({ name: e.target.value })} className="min-w-32" />
          </Td>
          <Td className="text-ink-2">{user.email}</Td>
          <Td>
            <Select aria-label="Role" value={draft.role} onChange={(e) => set({ role: e.target.value as Role })} disabled={isSelf}>
              {RoleSchema.options.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </Select>
          </Td>
          <Td>
            <Select aria-label="Team" value={draft.teamId} onChange={(e) => set({ teamId: e.target.value })}>
              <option value="">No team</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
          </Td>
          <Td>
            <Checkbox aria-label="Active" checked={draft.active} disabled={isSelf} onChange={(e) => set({ active: e.target.checked })} />
          </Td>
          <Td>
            <Input
              aria-label="Monthly limit (USD)"
              type="number"
              min={0}
              step="0.01"
              placeholder="Default"
              value={draft.limit}
              onChange={(e) => set({ limit: e.target.value })}
              className="w-24 text-right"
            />
          </Td>
          <Td>
            <Checkbox aria-label="Cloud STT allowed" checked={draft.cloudSttAllowed} onChange={(e) => set({ cloudSttAllowed: e.target.checked })} />
          </Td>
          <Td className="whitespace-nowrap">
            <Button size="sm" variant="primary" loading={save.isPending} onClick={submit}>
              Save
            </Button>{' '}
            <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>
              Cancel
            </Button>
          </Td>
        </tr>
        {!!error && (
          <tr>
            <td colSpan={8} className="px-3 pb-2">
              <ErrorBanner error={error} />
            </td>
          </tr>
        )}
      </>
    );
  }

  return (
    <Tr className={user.active ? undefined : 'opacity-60'}>
      <Td className="whitespace-nowrap">
        {user.name} {isSelf && <span className="text-xs text-muted">(you)</span>}
      </Td>
      <Td className="text-ink-2">
        {user.email} {user.invited && <Badge tone="warn">Invited</Badge>}
      </Td>
      <Td>
        <Badge tone={user.role === 'admin' ? 'accent' : 'neutral'}>{ROLE_LABELS[user.role]}</Badge>
      </Td>
      <Td className="text-ink-2">{user.teamName ?? '—'}</Td>
      <Td>{user.active ? <Badge tone="good">Active</Badge> : <Badge>Inactive</Badge>}</Td>
      <Td className="text-right tabular-nums">
        {user.monthlyLimitUsd == null ? <span className="text-muted">Default</span> : formatUsd(user.monthlyLimitUsd)}
      </Td>
      <Td>{user.cloudSttAllowed ? 'Allowed' : <span className="text-muted">No</span>}</Td>
      {editable && (
        <Td className="text-right">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setError(null);
              setDraft(toUserDraft(user));
            }}
          >
            Edit
          </Button>
          {!isSelf && !user.invited && (
            <Button
              size="sm"
              variant="ghost"
              loading={resetPassword.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    `Reset ${user.name}'s password? They will be signed out and must sign up again with ${user.email} to choose a new password.`,
                  )
                )
                  resetPassword.mutate();
              }}
            >
              Reset password
            </Button>
          )}
        </Td>
      )}
    </Tr>
  );
}

function InviteCard({ teams }: { teams: TeamDTO[] }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ email: '', name: '', role: 'rep' as Role, teamId: '' });
  const [validation, setValidation] = useState<string | null>(null);
  const invite = useMutation({
    mutationFn: usersApi.invite,
    onSuccess: () => {
      setForm({ email: '', name: '', role: 'rep', teamId: '' });
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const parsed = InviteBodySchema.safeParse({ ...form, email: form.email.trim(), name: form.name.trim(), teamId: form.teamId || null });
    if (!parsed.success) {
      setValidation('Enter a valid email and a name.');
      return;
    }
    setValidation(null);
    invite.mutate(parsed.data);
  };

  return (
    <Card title="Invite user" description="The person then signs up with this email address to claim the account.">
      <form className="space-y-3" onSubmit={submit}>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Email">
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Field>
          <Field label="Name">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label="Role">
            <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as Role })}>
              {RoleSchema.options.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Team">
            <Select value={form.teamId} onChange={(e) => setForm({ ...form, teamId: e.target.value })}>
              <option value="">No team</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        {validation && <p className="text-xs text-danger">{validation}</p>}
        <ErrorBanner error={invite.error} />
        {invite.isSuccess && <p className="text-xs text-good">Invitation created.</p>}
        <div className="flex justify-end">
          <Button type="submit" variant="primary" loading={invite.isPending}>
            Invite
          </Button>
        </div>
      </form>
    </Card>
  );
}

function TeamsCard({ teams, editable }: { teams: UseQueryResult<TeamDTO[]>; editable: boolean }) {
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['teams'] });
    void queryClient.invalidateQueries({ queryKey: ['users'] });
  };
  const [newName, setNewName] = useState('');
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState<TeamDTO | null>(null);

  const create = useMutation({
    mutationFn: (name: string) => teamsApi.create(name),
    onSuccess: () => {
      setNewName('');
      invalidate();
    },
  });
  const rename = useMutation({
    mutationFn: (r: { id: string; name: string }) => teamsApi.rename(r.id, r.name),
    onSuccess: () => {
      setRenaming(null);
      invalidate();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => teamsApi.remove(id),
    onSuccess: () => {
      setDeleting(null);
      invalidate();
    },
  });

  return (
    <Card title="Teams" bodyClassName="p-0">
      {teams.isError && <ErrorBanner error={teams.error} onRetry={() => void teams.refetch()} className="m-3" />}
      {teams.isLoading ? (
        <ListSkeleton />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Team</Th>
              <Th>Managers</Th>
              <Th className="text-right">Members</Th>
              {editable && <Th className="w-36" />}
            </tr>
          </thead>
          <tbody>
            {(teams.data ?? []).length === 0 && (
              <tr>
                <Td colSpan={4} className="text-muted">
                  No teams yet.
                </Td>
              </tr>
            )}
            {(teams.data ?? []).map((t) =>
              renaming?.id === t.id ? (
                <tr key={t.id}>
                  <Td colSpan={3}>
                    <form
                      className="flex gap-2"
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (renaming.name.trim()) rename.mutate({ id: t.id, name: renaming.name.trim() });
                      }}
                    >
                      <Input aria-label="Team name" value={renaming.name} onChange={(e) => setRenaming({ id: t.id, name: e.target.value })} autoFocus />
                      <Button type="submit" size="sm" variant="primary" loading={rename.isPending}>
                        Save
                      </Button>
                    </form>
                  </Td>
                  <Td>
                    <Button size="sm" variant="ghost" onClick={() => setRenaming(null)}>
                      Cancel
                    </Button>
                  </Td>
                </tr>
              ) : (
                <Tr key={t.id}>
                  <Td className="font-medium">{t.name}</Td>
                  <Td className="text-ink-2">{t.managers.map((m) => m.name).join(', ') || <span className="text-muted">None</span>}</Td>
                  <Td className="text-right tabular-nums">{t.memberCount}</Td>
                  {editable && (
                    <Td className="whitespace-nowrap text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          rename.reset();
                          setRenaming({ id: t.id, name: t.name });
                        }}
                      >
                        Rename
                      </Button>
                      <Button size="sm" variant="ghost" className="text-danger" onClick={() => setDeleting(t)}>
                        Delete
                      </Button>
                    </Td>
                  )}
                </Tr>
              ),
            )}
          </tbody>
        </Table>
      )}
      {editable && (
        <form
          className="flex gap-2 border-t border-line p-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (newName.trim()) create.mutate(newName.trim());
          }}
        >
          <Input aria-label="New team name" placeholder="New team name" value={newName} onChange={(e) => setNewName(e.target.value)} maxLength={120} />
          <Button type="submit" loading={create.isPending} disabled={!newName.trim()}>
            Create team
          </Button>
        </form>
      )}
      <ErrorBanner error={create.error ?? rename.error} className="m-3" />
      <p className="px-3 pb-3 text-xs text-muted">Managers are users with the Manager role in that team.</p>
      <ConfirmDialog
        open={!!deleting}
        title="Delete team"
        danger
        confirmLabel="Delete team"
        loading={remove.isPending}
        error={remove.error}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
        onCancel={() => {
          setDeleting(null);
          remove.reset();
        }}
      >
        Delete <strong>{deleting?.name}</strong>? Its {deleting?.memberCount ?? 0} members will have no team. Meetings are kept.
      </ConfirmDialog>
    </Card>
  );
}
